import { HyosungCMSMemberDoc, IssueTaskDoc, IssueTaskStatus, OrderStatusCode, StoreDoc } from '@gooduncles/gu-app-schema';
import { groupBy, sumBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { combineLatest, map } from 'rxjs';
import {
  ISSUE_TASK_STATUS_FOR_INVOICE,
  issueTaskCategoriesForSettlement,
} from 'src/constants/issue.constants';
import {
  observeIssueTasks,
  observeOrder,
  observeSettlementResult,
  observeStoreIssue,
} from 'src/utils/firebase/firebase-short-cut';
import { getPaymentInvoicesForStore } from 'src/utils/payment-util';

import { InvoiceCreationItem } from 'src/components/NewSettlement/InvoiceTable/InvoiceCreationTable';

import { UnpaidStoreItem } from '../components/UnpaidStoresModal';

interface UseInvoiceCreationDataParams {
  stores: StoreDoc[] | undefined;
  hyosungCMSMembers: HyosungCMSMemberDoc[] | undefined;
}

interface UseInvoiceCreationDataResult {
  invoiceCreationItems: InvoiceCreationItem[];
  inProgressTasks: IssueTaskDoc[];
  unpaidStoreItems: UnpaidStoreItem[];
  overpaidStoreItems: UnpaidStoreItem[];
  totalUnpaidInfo: {
    totalUnpaidAmount: number;
    unpaidStoreCount: number;
    totalOverpaidAmount: number;
    overpaidStoreCount: number;
  };
}

const useInvoiceCreationData = ({
  stores,
  hyosungCMSMembers,
}: UseInvoiceCreationDataParams): UseInvoiceCreationDataResult => {
  const [invoiceCreationItems, setInvoiceCreationItems] = useState<InvoiceCreationItem[]>([]);
  const [inProgressTasks, setInProgressTasks] = useState<IssueTaskDoc[]>([]);
  const [unpaidStoreItems, setUnpaidStoreItems] = useState<UnpaidStoreItem[]>([]);
  const [overpaidStoreItems, setOverpaidStoreItems] = useState<UnpaidStoreItem[]>([]);
  const [totalUnpaidInfo, setTotalUnpaidInfo] = useState({
    totalUnpaidAmount: 0,
    unpaidStoreCount: 0,
    totalOverpaidAmount: 0,
    overpaidStoreCount: 0,
  });

  useEffect(() => {
    if (!stores || !hyosungCMSMembers) return;

    // 미납금이 있으면서, 다시 청구가 필요한 결제결과
    const settlementResultObservable = observeSettlementResult([
      ['invoiceIdForUnpaid', '==', null],
      ['unpaidAmount', '!=', 0],
      ['deletedAt', '==', null],
    ]);
    const orderObservable = observeOrder([
      ['invoiceId', '==', null],
      ['orderStatus', '==', OrderStatusCode.DELIVERED],
    ]);
    const storeIssueObservable = observeStoreIssue([
      ['invoiceId', '==', null],
      ['status', '==', '완료'],
    ]).pipe(
      map((storeIssues) =>
        storeIssues.filter((issue) => issue.isDeleted === false && issue.message && issue.message.length > 0)
      )
    );
    const issueTaskObservable = observeIssueTasks([
      ['invoiceInfo.invoiceId', '==', null],
      ['category', 'in', issueTaskCategoriesForSettlement],
      ['status', 'in', ISSUE_TASK_STATUS_FOR_INVOICE],
    ]);

    const subscription = combineLatest([
      settlementResultObservable,
      orderObservable,
      storeIssueObservable,
      issueTaskObservable,
    ]).subscribe(([settlementResults, orders, storeIssues, issueTasks]) => {
      const resultsGroupByStoreId = groupBy(settlementResults, 'storeId');
      const ordersGroupByStoreId = groupBy(orders, 'storeId');
      const storeIssuesGroupByStoreId = groupBy(
        storeIssues,
        (issue) => stores.find((s) => s.storeCode === issue.storeCode)?._id ?? 'unknown'
      );

      const completedIssueTasks = issueTasks.filter((task) => task.status === IssueTaskStatus.COMPLETED);
      const inProgressIssueTasks = issueTasks.filter((task) => task.status === IssueTaskStatus.IN_PROGRESS);
      setInProgressTasks(inProgressIssueTasks);

      const issueTasksGroupByStoreId = groupBy(completedIssueTasks, 'storeId');

      // TODO: 매장이 아닌 결제정보를 기준으로 청구서를 생성하도록 변경하자.
      const items = stores
        .map((store) => {
          const unpaidResults = resultsGroupByStoreId[store._id] ?? [];
          const settlementResult = unpaidResults.find((result) => result.settledAt === store.latestSettledAt);
          const storeOrders = ordersGroupByStoreId[store._id] ?? [];
          const storeStoreIssues = storeIssuesGroupByStoreId[store._id] ?? [];
          const storeIssueTasks = issueTasksGroupByStoreId[store._id] ?? [];
          const hyosungCMSMember = hyosungCMSMembers.find((member) => member.storeCode === store.storeCode);

          // TODO: 결제 정보가 없는 매장에 대한 처리
          let errorMsg = store.paymentMethod && store.paymentVendor ? null : '결제정보가 없습니다.';
          if (store.paymentVendor === '효성CMS' && !hyosungCMSMember) {
            errorMsg = errorMsg ? errorMsg + ' 효성CMS 회원정보가 없습니다.' : '효성CMS 회원정보가 없습니다.';
          }

          const invoice = getPaymentInvoicesForStore(
            store,
            settlementResult,
            storeOrders,
            storeStoreIssues,
            storeIssueTasks,
            hyosungCMSMember
          );

          return {
            storeId: store._id,
            storeCode: store.storeCode,
            storeNickname: store.storeNickname,
            paymentVendor: store.paymentVendor,
            paymentMethod: store.paymentMethod,
            invoice,
            orders: storeOrders,
            issueTasks: storeIssueTasks,
            storeIssues: storeStoreIssues,
            errorMsg,
            resultCreatedAt: settlementResult?._timeCreate ?? null,
          };
        })
        .filter((item) => item.invoice) as InvoiceCreationItem[];

      setInvoiceCreationItems(items);

      // 미수금 통계 (미납: unpaidAmount > 0, 초과납부: unpaidAmount < 0)
      const unpaidSettlementResults = settlementResults.filter((result) => result.unpaidAmount > 0);
      const overpaidSettlementResults = settlementResults.filter((result) => result.unpaidAmount < 0);

      setTotalUnpaidInfo({
        totalUnpaidAmount: sumBy(unpaidSettlementResults, 'unpaidAmount'),
        unpaidStoreCount: unpaidSettlementResults.length,
        totalOverpaidAmount: Math.abs(sumBy(overpaidSettlementResults, 'unpaidAmount')),
        overpaidStoreCount: overpaidSettlementResults.length,
      });

      // 미수 매장 목록 생성
      const unpaidItems: UnpaidStoreItem[] = unpaidSettlementResults.map((result) => {
        const store = stores.find((s) => s._id === result.storeId);
        return {
          key: result._id,
          storeId: result.storeId,
          storeNickname: store?.storeNickname ?? result.storeId,
          settledAt: result.settledAt,
          invoiceAmount: result.invoiceAmount,
          unpaidAmount: result.unpaidAmount,
        };
      });
      setUnpaidStoreItems(unpaidItems);

      // 초과납부 매장 목록 생성
      const overpaidItems: UnpaidStoreItem[] = overpaidSettlementResults.map((result) => {
        const store = stores.find((s) => s._id === result.storeId);
        return {
          key: result._id,
          storeId: result.storeId,
          storeNickname: store?.storeNickname ?? result.storeId,
          settledAt: result.settledAt,
          invoiceAmount: result.invoiceAmount,
          unpaidAmount: result.unpaidAmount,
        };
      });
      setOverpaidStoreItems(overpaidItems);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [stores, hyosungCMSMembers]);

  return {
    invoiceCreationItems,
    inProgressTasks,
    unpaidStoreItems,
    overpaidStoreItems,
    totalUnpaidInfo,
  };
};

export default useInvoiceCreationData;
